import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { Booking } from "../../interfaces/booking.interface";
import { convertToRupiah } from "../../utils/convertToRupiah";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#111827",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  logo: { width: 150, height: 56, objectFit: "contain", objectPositionX: 0 },
  receiptMeta: { alignItems: "flex-end" },
  receiptTitle: { fontSize: 18, fontFamily: "Helvetica-Bold", marginBottom: 4 },
  metaRow: { flexDirection: "row", marginBottom: 2 },
  metaLabel: { fontFamily: "Helvetica-Bold", marginRight: 4 },
  metaValue: { color: "#6b7280" },
  twoCol: { flexDirection: "row", gap: 8, marginBottom: 12 },
  col: { flex: 1 },
  colRight: { flex: 1, alignItems: "flex-end" },
  colTitle: { fontFamily: "Helvetica-Bold", marginBottom: 2 },
  colName: { fontSize: 12, marginBottom: 2 },
  colText: { color: "#6b7280", marginBottom: 2 },
  colTextRight: { color: "#6b7280", marginBottom: 2, textAlign: "right" },
  sectionTitle: { fontSize: 12, fontFamily: "Helvetica-Bold", marginBottom: 4 },
  divider: { borderBottomWidth: 1, borderBottomColor: "#000", marginBottom: 8 },
  infoRow: { flexDirection: "row", marginBottom: 3 },
  infoLabel: { width: "25%", fontFamily: "Helvetica-Bold" },
  infoColon: { width: 8 },
  infoValue: { flex: 1, color: "#6b7280" },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingVertical: 4,
    fontFamily: "Helvetica-Bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 4,
  },
  tableGroupRow: {
    paddingVertical: 3,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#6b7280",
  },
  colItem: { width: "50%", paddingRight: 4 },
  colQty: { width: "20%", textAlign: "center" },
  colTotal: { width: "30%", textAlign: "right" },
  summaryRow: {
    flexDirection: "row",
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
  },
  summaryLabel: { flex: 1, textAlign: "center", fontFamily: "Helvetica-Bold" },
  summaryValue: { width: "30%", textAlign: "right", fontFamily: "Helvetica-Bold" },
  summaryLabelLg: {
    flex: 1,
    textAlign: "center",
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
  },
  summaryValueLg: {
    width: "30%",
    textAlign: "right",
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
  },
  paymentRow: {
    flexDirection: "row",
    paddingVertical: 4,
    marginTop: 2,
  },
});

const fmtDate = (s: string) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "-";

const fmtDeparture = (s: string) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "";

const fmtTime = (s: string) =>
  s ? new Date(s).toLocaleTimeString("en-GB", { timeStyle: "short" }) : "";

interface Props {
  booking: Booking;
  logoDataUrl: string;
  providerImageUrl?: string;
  restrictedProviderImages?: Record<string, string | null>;
}

interface PriceSummaryItem {
  count: number;
  total: number;
  label: string;
}

export const ReceiptPdf: React.FC<Props> = ({ booking, logoDataUrl, providerImageUrl, restrictedProviderImages }) => {
  const invoice = booking.invoice;

  // Build passenger price breakdown
  const breakdown: Record<string, PriceSummaryItem> = {};
  booking.tickets.forEach((ticket) => {
    const isForeigner = ticket.nationality?.toUpperCase() !== "INDONESIA";
    const key = isForeigner ? `${ticket.rateCategory}-foreigner` : ticket.rateCategory;
    const label = isForeigner
      ? `${ticket.rateCategory.charAt(0).toUpperCase()}${ticket.rateCategory.slice(1)} (Foreigner)`
      : `${ticket.rateCategory.charAt(0).toUpperCase()}${ticket.rateCategory.slice(1)}`;
    if (!breakdown[key]) {
      breakdown[key] = { count: 0, total: 0, label };
    }
    breakdown[key].count += 1;
    breakdown[key].total += ticket.price;
  });
  const breakdownItems = Object.values(breakdown);

  const numPax = booking.tickets.length;
  const outboundTax = booking.taxAmount ?? 0;
  const outboundSurcharge = booking.surchargeAmount ?? 0;
  const returnTax = booking.returnTaxAmount ?? 0;
  const returnSurcharge = booking.returnSurchargeAmount ?? 0;

  const hasOutboundFees = booking.taxAmount != null || booking.surchargeAmount != null;
  const hasReturnFees =
    booking.isTwoWay &&
    (booking.returnTaxAmount != null || booking.returnSurchargeAmount != null);

  const subtotal =
    breakdownItems.reduce((s, item) => s + item.total, 0) +
    (booking.taxAmount != null ? outboundTax : 0) +
    (booking.surchargeAmount != null ? outboundSurcharge : 0) +
    (booking.isTwoWay && booking.returnTaxAmount != null ? returnTax : 0) +
    (booking.isTwoWay && booking.returnSurchargeAmount != null ? returnSurcharge : 0);
  const platformFee = invoice?.transactionFee ?? 0;
  const grandTotal = subtotal + platformFee;
  const pointsUsed = invoice?.balanceAmount ?? 0;


  const recipientName = booking.invoiceRecipientName ?? "";
  const recipientEmail = booking.invoiceRecipientEmail ?? "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src={logoDataUrl} style={styles.logo} />
          <View style={styles.receiptMeta}>
            <Text style={styles.receiptTitle}>Receipt</Text>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>No. #</Text>
              <Text style={styles.metaValue}>
                {booking.bookingId && booking.bookingId.length > 0
                  ? booking.bookingId
                  : booking._id}
              </Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Date</Text>
              <Text style={styles.metaValue}>{fmtDate(booking.createdAt)}</Text>
            </View>
          </View>
        </View>

        {/* Company + Recipient */}
        <View style={styles.twoCol}>
          <View style={styles.col}>
            <Text style={styles.colTitle}>Company Info:</Text>
            <Text style={styles.colName}>LUNA GLOBAL TRAVELINDO</Text>
            <Text style={styles.colText}>
              Terminal Ferry International Office No 10 Batam Centre Kota Batam,
              Kepulauan Riau{"\n"}(+62) 852-9999-9820
            </Text>
          </View>
          <View style={styles.colRight}>
            <Text style={[styles.colTitle, { textAlign: "right" }]}>Recipient Info:</Text>
            <Text style={[styles.colName, { textAlign: "right" }]}>{recipientName}</Text>
            <Text style={styles.colTextRight}>{recipientEmail}</Text>
          </View>
        </View>

        {/* Departure Information */}
        <View style={{ marginBottom: 12 }}>
          <Text style={styles.sectionTitle}>Departure Information</Text>
          <View style={styles.divider} />
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Provider</Text>
            <Text style={styles.infoColon}>:</Text>
            <View style={[styles.infoValue, { flexDirection: "row", alignItems: "center" }]}>
              {providerImageUrl && (
                <Image
                  src={providerImageUrl}
                  style={{ width: 12, height: 12, borderRadius: 6, marginRight: 4 }}
                />
              )}
              <Text>{booking.schedule.provider.name}</Text>
            </View>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Route</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValue}>
              {booking.schedule.origin.port} - {booking.schedule.destination.port}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Date</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValue}>{fmtDeparture(booking.schedule.departure)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Time</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValue}>{fmtTime(booking.schedule.departure)}</Text>
          </View>
        </View>

        {/* Return Information — isTwoWay only */}
        {booking.isTwoWay && (
          <View style={{ marginBottom: 12 }}>
            <Text style={styles.sectionTitle}>Return Information</Text>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Type</Text>
              <Text style={styles.infoColon}>:</Text>
              <Text style={styles.infoValue}>Open Return Ticket</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Route</Text>
              <Text style={styles.infoColon}>:</Text>
              <Text style={styles.infoValue}>
                {booking.schedule.destination.port} - {booking.schedule.origin.port}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Valid for</Text>
              <Text style={styles.infoColon}>:</Text>
              <Text style={styles.infoValue}>90 days from purchase</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Valid with</Text>
              <Text style={styles.infoColon}>:</Text>
              {booking.isRestrictedReturn && booking.restrictedReturnProviders?.length ? (
                <View style={{ flex: 1 }}>
                  {booking.restrictedReturnProviders.map((p) => {
                    const img = restrictedProviderImages?.[p._id] ?? null;
                    return (
                      <View key={p._id} style={{ flexDirection: "row", alignItems: "center", marginBottom: 2 }}>
                        {img !== null ? (
                          <Image src={img} style={{ width: 12, height: 12, marginRight: 4 }} />
                        ) : (
                          <View style={{ width: 12, height: 12, marginRight: 4 }} />
                        )}
                        <Text style={styles.infoValue}>{p.name ?? p._id}</Text>
                      </View>
                    );
                  })}
                </View>
              ) : (
                <Text style={styles.infoValue}>Any provider</Text>
              )}
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}></Text>
              <Text style={styles.infoColon}></Text>
              <Text style={styles.infoValue}>Subject to seat availability at counter</Text>
            </View>
          </View>
        )}

        {/* Price Table */}
        <View style={styles.tableHeader}>
          <Text style={styles.colItem}>Item</Text>
          <Text style={styles.colQty}>Quantity</Text>
          <Text style={styles.colTotal}>Total</Text>
        </View>

        {/* Fare group label — round-trip only */}
        {booking.isTwoWay && (
          <View style={styles.tableGroupRow}>
            <Text>Fare</Text>
          </View>
        )}

        {/* Passenger rows */}
        {breakdownItems.map((item) => (
          <View key={item.label} style={styles.tableRow}>
            <Text style={styles.colItem}>{item.label}</Text>
            <Text style={styles.colQty}>x{item.count}</Text>
            <Text style={styles.colTotal}>{convertToRupiah(item.total)}</Text>
          </View>
        ))}

        {/* Outbound Fees group label — round-trip only */}
        {hasOutboundFees && booking.isTwoWay && (
          <View style={styles.tableGroupRow}>
            <Text>Outbound Fees</Text>
          </View>
        )}
        {booking.taxAmount != null && (
          <View style={styles.tableRow}>
            <Text style={styles.colItem}>
              {booking.schedule.origin.country} Sea Port Tax
            </Text>
            <Text style={styles.colQty}>x{numPax}</Text>
            <Text style={styles.colTotal}>{convertToRupiah(outboundTax)}</Text>
          </View>
        )}
        {booking.surchargeAmount != null && (
          <View style={styles.tableRow}>
            <Text style={styles.colItem}>
              {booking.schedule.origin.country} Surcharge
            </Text>
            <Text style={styles.colQty}>x{numPax}</Text>
            <Text style={styles.colTotal}>{convertToRupiah(outboundSurcharge)}</Text>
          </View>
        )}

        {/* Return Fees group label + rows — round-trip only */}
        {hasReturnFees && (
          <View style={styles.tableGroupRow}>
            <Text>Return Fees</Text>
          </View>
        )}
        {booking.isTwoWay && booking.returnTaxAmount != null && (
          <View style={styles.tableRow}>
            <Text style={styles.colItem}>
              {booking.schedule.destination.country} Return Sea Port Tax
            </Text>
            <Text style={styles.colQty}>x{numPax}</Text>
            <Text style={styles.colTotal}>{convertToRupiah(returnTax)}</Text>
          </View>
        )}
        {booking.isTwoWay && booking.returnSurchargeAmount != null && (
          <View style={styles.tableRow}>
            <Text style={styles.colItem}>
              {booking.schedule.destination.country} Return Surcharge
            </Text>
            <Text style={styles.colQty}>x{numPax}</Text>
            <Text style={styles.colTotal}>{convertToRupiah(returnSurcharge)}</Text>
          </View>
        )}

        {/* Payment Summary */}
        <View style={[styles.summaryRow, { borderTopWidth: 1, borderTopColor: "#000" }]}>
          <Text style={{ flex: 1 }}></Text>
          <Text style={styles.summaryLabel}>Subtotal</Text>
          <Text style={styles.summaryValue}>{convertToRupiah(subtotal)}</Text>
        </View>

        {platformFee > 0 && (
          <View style={styles.summaryRow}>
            <Text style={{ flex: 1 }}></Text>
            <Text style={styles.summaryLabel}>Platform fee</Text>
            <Text style={styles.summaryValue}>{convertToRupiah(platformFee)}</Text>
          </View>
        )}

        <View style={[styles.summaryRow, { borderTopWidth: 1, borderTopColor: "#000" }]}>
          <Text style={{ flex: 1 }}></Text>
          <Text style={styles.summaryLabelLg}>Total</Text>
          <Text style={styles.summaryValueLg}>{convertToRupiah(grandTotal)}</Text>
        </View>

        {pointsUsed > 0 && (
          <View style={styles.summaryRow}>
            <Text style={{ flex: 1 }}></Text>
            <Text style={styles.summaryLabel}>Points Used</Text>
            <Text style={styles.summaryValue}>- {convertToRupiah(pointsUsed)}</Text>
          </View>
        )}

        <View style={[styles.paymentRow, { borderTopWidth: 1, borderTopColor: "#000" }]}>
          <Text style={{ flex: 1 }}></Text>
          <Text style={[styles.summaryLabelLg, { fontSize: 13 }]}>PAYMENT AMOUNT</Text>
          <Text style={[styles.summaryValueLg, { fontSize: 13 }]}>
            {convertToRupiah(grandTotal - pointsUsed)}
          </Text>
        </View>

        {(invoice?.paymentGatewayAmount ?? 0) > 0 && invoice?.paymentChannel && (
          <View style={styles.paymentRow}>
            <Text style={{ flex: 1 }}></Text>
            <Text style={[styles.summaryValue, { fontFamily: "Helvetica" }]}>
              Paid with {invoice.paymentChannel.replace(/_/g, " ")}
            </Text>
          </View>
        )}
      </Page>
    </Document>
  );
};
